import React from "react";
import { Campaign } from "../../services";

interface CampaignActionsProps {
  campaign: Campaign;
  onEdit: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
  onPageChange: (page: string) => void;
  onShowDeleteModal: (campaign: Campaign) => void;
  isAdmin: boolean;
}

const CampaignActions: React.FC<CampaignActionsProps> = ({
  campaign,
  onEdit,
  onDelete,
  onPageChange,
  onShowDeleteModal,
  isAdmin,
}) => {
  const now = new Date();
  const startDate = new Date(campaign.start_date);
  const endDate = new Date(campaign.end_date);

  // So sánh chỉ theo ngày, không theo giờ
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startDay = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  const endDay = new Date(
    endDate.getFullYear(),
    endDate.getMonth(),
    endDate.getDate()
  );

  const isNotStarted = startDay > today;
  const isExpired = endDay < today;
  const isActive = !isNotStarted && !isExpired;
  const isOutOfCodes = false; // TODO: Add usedCodes field to API response

  const isFullyEnded = isExpired;
  // Chiến dịch đã kết thúc - vẫn cho phép chỉnh sửa
  const canViewOnly = false; // Luôn cho phép chỉnh sửa
  // Tất cả chiến dịch đều có thể chỉnh sửa (bao gồm cả đã kết thúc)
  const canEdit = true;

  const handleEdit = () => {
    // Tất cả chiến dịch đều có thể chỉnh sửa
    onPageChange(`campaigns/edit/${campaign._id}`);
  };

  return (
    <div className="flex items-center space-x-2 lg:space-x-3">
      {/* Button chỉnh sửa hoặc xem chi tiết tùy theo role - Admin: edit icon, User: eye icon */}
      <button
        onClick={handleEdit}
        className={`p-1.5 lg:p-2 rounded-md transition-colors ${
          isAdmin
            ? "text-blue-500 hover:text-blue-700 hover:bg-blue-50"
            : "text-blue-500 hover:text-blue-700 hover:bg-blue-50"
        }`}
        title={isAdmin ? "Chỉnh sửa chiến dịch" : "Xem chi tiết chiến dịch"}
      >
        {isAdmin ? (
          /* Edit icon - cho Admin */
          <svg
            className="w-4 h-4 lg:w-5 lg:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
        ) : (
          /* Eye icon - cho User */
          <svg
            className="w-4 h-4 lg:w-5 lg:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
            />
          </svg>
        )}
      </button>

      {/* Xóa button - chỉ hiển thị cho Admin và chiến dịch có trạng thái tắt (không được xóa nếu đã kết thúc) */}
      {isAdmin && (
        <button
          onClick={
            !isFullyEnded && campaign.status === 2
              ? () => onShowDeleteModal(campaign)
              : undefined
          }
          disabled={isFullyEnded || campaign.status !== 2}
          className={`p-1.5 lg:p-2 rounded-md transition-colors ${
            !isFullyEnded && campaign.status === 2
              ? "text-red-600 hover:text-red-900 hover:bg-red-50"
              : "text-gray-400 cursor-not-allowed opacity-50"
          }`}
          title={
            isFullyEnded
              ? "Không thể xóa chiến dịch đã kết thúc"
              : campaign.status === 2
              ? "Xóa chiến dịch (trạng thái tắt)"
              : "Không thể xóa chiến dịch đang hoạt động"
          }
        >
          <svg
            className="w-4 h-4 lg:w-5 lg:h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default CampaignActions;
